import api from './api';

const FAVORITES_STORAGE_KEY = 'nukki_favorites';

/**
 * Get favorites from localStorage (for non-authenticated users)
 */
export const getLocalFavorites = () => {
  try {
    const favorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
    return favorites ? JSON.parse(favorites) : [];
  } catch (error) {
    console.error('Error getting local favorites:', error);
    return [];
  }
};

/**
 * Save favorites to localStorage
 */
export const setLocalFavorites = (propertyIds) => {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(propertyIds));
  } catch (error) {
    console.error('Error saving local favorites:', error);
  }
};

/**
 * Add property to local favorites
 */
export const addLocalFavorite = (propertyId) => {
  const favorites = getLocalFavorites();
  if (!favorites.includes(propertyId)) {
    favorites.push(propertyId);
    setLocalFavorites(favorites);
  }
  return favorites;
};

/**
 * Remove property from local favorites
 */
export const removeLocalFavorite = (propertyId) => {
  const favorites = getLocalFavorites();
  const updated = favorites.filter(id => id !== propertyId);
  setLocalFavorites(updated);
  return updated;
};

/**
 * Check if property is in local favorites
 */
export const isLocalFavorite = (propertyId) => {
  const favorites = getLocalFavorites();
  return favorites.includes(propertyId);
};

/**
 * Clear local favorites (used after sync)
 */
export const clearLocalFavorites = () => {
  localStorage.removeItem(FAVORITES_STORAGE_KEY);
};

/**
 * Add property to favorites (backend - authenticated users)
 */
export const addFavorite = async (propertyId) => {
  try {
    const response = await api.post(`/api/properties/${propertyId}/favorite`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Remove property from favorites (backend - authenticated users)
 */
export const removeFavorite = async (propertyId) => {
  try {
    const response = await api.delete(`/api/properties/${propertyId}/favorite`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all user favorites (backend - authenticated users)
 */
export const getFavorites = async () => {
  try {
    const response = await api.get('/api/favorites');
    // Ensure we always return a consistent structure
    return {
      favorites: response.data?.favorites || [],
      total: response.data?.total || 0
    };
  } catch (error) {
    console.error('Error fetching favorites:', error);
    // Return empty structure on error instead of throwing
    return { favorites: [], total: 0 };
  }
};

/**
 * Check if property is favorite (backend - authenticated users)
 */
export const checkIsFavorite = async (propertyId) => {
  try {
    const response = await api.get(`/api/properties/${propertyId}/is-favorite`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Sync local favorites to backend after login
 */
export const syncLocalFavorites = async () => {
  try {
    const localFavorites = getLocalFavorites();

    if (localFavorites.length === 0) {
      return { synced: 0 };
    }

    // Add all local favorites to backend
    const promises = localFavorites.map(propertyId =>
      addFavorite(propertyId).catch(err => {
        console.error(`Failed to sync favorite ${propertyId}:`, err);
        return null;
      })
    );

    await Promise.all(promises);

    // Clear local storage after sync
    clearLocalFavorites();

    return { synced: localFavorites.length };
  } catch (error) {
    console.error('Error syncing local favorites:', error);
    throw error;
  }
};

/**
 * Toggle favorite (works for both authenticated and non-authenticated users)
 */
export const toggleFavorite = async (propertyId, isAuthenticated) => {
  if (isAuthenticated) {
    // User is authenticated - use backend
    try {
      // Check current status with defensive access
      const currentFavorites = await getFavorites();
      const favoritesList = currentFavorites?.favorites || [];
      const isFavorite = favoritesList.some(p => p.id === propertyId);

      if (isFavorite) {
        await removeFavorite(propertyId);
        return { isFavorite: false };
      } else {
        await addFavorite(propertyId);
        return { isFavorite: true };
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  } else {
    // User is not authenticated - use localStorage
    const isFavorite = isLocalFavorite(propertyId);

    if (isFavorite) {
      removeLocalFavorite(propertyId);
      return { isFavorite: false };
    } else {
      addLocalFavorite(propertyId);
      return { isFavorite: true };
    }
  }
};

export default {
  // Local storage functions
  getLocalFavorites,
  setLocalFavorites,
  addLocalFavorite,
  removeLocalFavorite,
  isLocalFavorite,
  clearLocalFavorites,

  // Backend functions
  addFavorite,
  removeFavorite,
  getFavorites,
  checkIsFavorite,
  syncLocalFavorites,

  // Combined function
  toggleFavorite,
};
